import React, { useState, useRef } from 'react'
import { Translate } from 'react-localize-redux'
import PropTypes from 'prop-types'
import {
  Card,
  Tabs,
  Tab,
  Divider,
  Grid,
  Button,
  Hidden,
} from '@material-ui/core'
import { Input } from 'components'
import { makeStyles } from '@material-ui/core/styles'
import { players as playersModule } from 'redux/modules'
import { connect } from 'react-redux'
import TabPanel from 'components/material/TabPanel'
import useTeamFeature from 'hooks/useTeamFeature'
import AddEditPlayerStrengthWeakness from './AddEditPlayerStrengthWeakness'
import PlayerAnalysisList from './PlayerAnalysisList'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    '& .MuiGrid-grid-md-6': {
      maxWidth: '49.2%',
      flexBasis: '49.2%',
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
        flexBasis: '100%',
      },
    },
  },
  textarea: {
    '& .MuiInputBase-root': {
      height: 'unset',
      paddingTop: 'unset',
      paddingBottom: 'unset',
    },
  },
}))

const PlayerDevelopmentTracking = ({
  onChange,
  development_tracking: developmentTracking,
  createPlayerAnalysis,
  updatePlayerAnalysis,
  deletePlayerAnalysis,
  fetchTeamPlayer,
  player,
  handleSave,
}) => {
  const [analysisUuid, setAnalysisUuid] = useState('')
  const [note, setNote] = useState(null)
  const [open, setOpen] = useState(false)
  const [selectedValue, selectedValueChange] = useState('strength')
  const DBStrength = player.strengths_weaknesses
    .filter((s) => s.type === 'strength')
    .map((st) => {
      return { uuid: st.uuid, name: st.note }
    })
  const DBWeakness = player.strengths_weaknesses
    .filter((w) => w.type === 'weakness')
    .map((weak) => {
      return { uuid: weak.uuid, name: weak.note }
    })
  const [strengths, setStrengths] = useState(DBStrength)
  const [weaknesses, setWeaknesses] = useState(DBWeakness)
  const nameRef = useRef()
  const classes = useStyles()

  const { enabled: hasPlayerStatisticsEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.player_statistics_enabled
  )

  const onFieldChange = ({ target: { value } }) =>
    onChange('development_tracking', value)

  const [value, setValue] = useState(hasPlayerStatisticsEnabled ? 1 : 0)

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleClickOpenfor = (value1) => {
    selectedValueChange(value1)
    setNote('')
    setAnalysisUuid('')
    handleClickOpen()
  }

  const handleClickOpenEdit = (analysisType, analysisNote, uuid) => {
    setNote(analysisNote)
    selectedValueChange(analysisType)
    setAnalysisUuid(uuid)
    handleClickOpen()
  }

  const deleteRecord = (uuid) => {
    const strengthItems = strengths.filter((strength) => strength.uuid !== uuid)
    const weaknessItems = weaknesses.filter(
      (weakness) => weakness.uuid !== uuid
    )
    setStrengths(strengthItems)
    setWeaknesses(weaknessItems)
    deletePlayerAnalysis(uuid)
  }

  return (
    <>
      <Card className={classes.root}>
        <Tabs value={value} onChange={handleChange}>
          {hasPlayerStatisticsEnabled && (
            <Tab label={<Translate id="team.development-tracking" />} />
          )}
          <Tab label={<Translate id="team.player-analysis" />} />
        </Tabs>
        <Divider />
        {hasPlayerStatisticsEnabled && (
          <TabPanel value={value} index={0}>
            <Grid container spacing={2}>
              <Input
                name="development_tracking"
                value={developmentTracking || ''}
                label={<Translate id="matches.additional-info" />}
                multiline
                minRows={8}
                onChange={onFieldChange}
                className={classes.textarea}
              />
              <Button variant="contained" color="primary" onClick={handleSave}>
                <Translate id="button.save" />
              </Button>
            </Grid>
          </TabPanel>
        )}
        <TabPanel value={value} index={hasPlayerStatisticsEnabled ? 1 : 0}>
          {open && (
            <AddEditPlayerStrengthWeakness
              open={open}
              selectedValue={selectedValue}
              onClose={handleClose}
              createPlayerAnalysis={createPlayerAnalysis}
              player={{ ...player }}
              nameRef={nameRef}
              updatePlayerAnalysis={updatePlayerAnalysis}
              fetchTeamPlayer={fetchTeamPlayer}
              setStrengths={setStrengths}
              setWeaknesses={setWeaknesses}
              analysisUuid={analysisUuid}
              note={note}
              setNote={setNote}
            />
          )}
          <Grid container spacing={2}>
            <PlayerAnalysisList
              entities={strengths}
              handleClickOpenEdit={handleClickOpenEdit}
              handleClickOpenfor={handleClickOpenfor}
              deleteRecord={deleteRecord}
              label={<Translate id="team.strengths" />}
              type="strength"
            />
            <Hidden smDown>
              <Divider
                orientation="vertical"
                flexItem
                style={{ marginLeft: 2, marginRight: 2 }}
              />
            </Hidden>
            <Hidden smUp>
              <Divider
                style={{ marginTop: 10, marginBottom: 10, width: '100%' }}
              />
            </Hidden>
            <PlayerAnalysisList
              entities={weaknesses}
              handleClickOpenEdit={handleClickOpenEdit}
              handleClickOpenfor={handleClickOpenfor}
              deleteRecord={deleteRecord}
              label={<Translate id="team.weaknesses" />}
              type="weakness"
            />
          </Grid>
        </TabPanel>
      </Card>
    </>
  )
}

PlayerDevelopmentTracking.defaultProps = {
  development_tracking: '',
}

PlayerDevelopmentTracking.propTypes = {
  onChange: PropTypes.func.isRequired,
  createPlayerAnalysis: PropTypes.func.isRequired,
  updatePlayerAnalysis: PropTypes.func.isRequired,
  deletePlayerAnalysis: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  development_tracking: PropTypes.string,
  fetchTeamPlayer: PropTypes.func.isRequired,
  player: PropTypes.shape().isRequired,
}

export default connect(
  ({
    router: {
      location: { fromUrl, fromPathname },
    },
    players,
  }) => ({
    fromUrl,
    fromPathname,
    player: players.current,
  }),
  {
    createPlayerAnalysis: playersModule.createPlayerAnalysis,
    updatePlayerAnalysis: playersModule.updatePlayerAnalysis,
    deletePlayerAnalysis: playersModule.deletePlayerAnalysis,
  }
)(PlayerDevelopmentTracking)
